import styled from '@emotion/styled';
import { Divider, tokens, useTippy } from '@hotjar/hotsauce';
import { NavDropdown, NavList } from '@hotjar/navigation/src/components/NavDropdown';
import { Position } from '@hotjar/navigation/src/events/Position';
import type { NavigationEvent } from '@hotjar/navigation/src/events/types';

import { useNavigation } from '@hotjar/navigation/src/hooks/useNavigation';
import type { EventEmitter, NavigationProps, User } from '@hotjar/navigation/src/types';
import { useTranslation } from 'react-i18next';
import { AvatarButton } from './AvatarButton';

import { ProfileSummary } from './ProfileSummary';

export interface AccountMenuProps extends Pick<User, 'name' | 'avatar' | 'email'> {
  className?: string;
  emitter: EventEmitter;
  isAccountUpgradable?: NavigationProps['isAccountUpgradable'];
  isEngageButtonActive?: boolean;
  onOpenEngage: () => void;
}

const StyledDropdown = styled(NavDropdown)`
  width: 300px;
`;

const HighlightedItem = styled(NavList.Item)`
  color: ${tokens.color.link};
`;

const eventPosition: NavigationEvent = Object.freeze({ position: Position.accountMenu });

export const AccountMenu = ({
  className,
  emitter,
  isAccountUpgradable,
  isEngageButtonActive,
  onOpenEngage,
  ...userProps
}: AccountMenuProps): JSX.Element => {
  const { t } = useTranslation('navigation');
  const tippyInstance = useTippy();

  const { routes, buildHrefFromRoute } = useNavigation();

  return (
    <StyledDropdown
      className={className}
      trigger={<AvatarButton userName={userProps.name} src={userProps.avatar || undefined} />}
      onTrigger={() => {
        emitter.emit('clickedAccountMenu', { position: Position.primaryNavigation });
      }}
      onCreate={tippyInstance.set}
    >
      <ProfileSummary
        {...userProps}
        onClick={() => {
          emitter.emit('clickedProfileSummary', { position: Position.accountMenu });
        }}
      />

      <NavList role="menu">
        <NavList.LinkItem
          icon="user_avatar"
          onClick={() => {
            tippyInstance.close();
            emitter.emit('clickedViewProfile', eventPosition);
          }}
          href={buildHrefFromRoute(routes.PROFILE.DETAILS.path)}
        >
          {t('AccountMenu.viewProfile')}
        </NavList.LinkItem>

        <NavList.LinkItem
          icon="cog"
          onClick={() => {
            tippyInstance.close();
            emitter.emit('clickedAccountSettings', eventPosition);
          }}
          href={buildHrefFromRoute(routes.SITE.LIST.path)}
        >
          {t('AccountMenu.accountSettings')}
        </NavList.LinkItem>

        <NavList.LinkItem
          icon="puzzle"
          onClick={() => {
            tippyInstance.close();
            emitter.emit('clickedIntegrations', eventPosition);
          }}
          href={buildHrefFromRoute(routes.SETTINGS.INTEGRATIONS.path)}
        >
          {t('AccountMenu.integrations')}
        </NavList.LinkItem>

        <NavList.LinkItem
          icon="billing_card"
          onClick={() => {
            tippyInstance.close();
            emitter.emit('clickedBilling', eventPosition);
          }}
          href={buildHrefFromRoute(routes.SETTINGS.BILLING.path)}
        >
          {t('AccountMenu.billing')}
        </NavList.LinkItem>

        <Divider />

        <NavList.LinkItem
          icon="user_add"
          onClick={() => {
            tippyInstance.close();
            emitter.emit('clickedInviteMembers', eventPosition);
          }}
          href={buildHrefFromRoute(routes.TEAM.INVITE.path)}
        >
          {t('AccountMenu.inviteMembers')}
        </NavList.LinkItem>

        <NavList.LinkItem
          icon="gift"
          onClick={() => {
            tippyInstance.close();
            emitter.emit('clickedReferAndEarn', eventPosition);
          }}
          href={buildHrefFromRoute(routes.PROFILE.REFERRALS.path)}
        >
          {t('AccountMenu.referAndEarn')}
        </NavList.LinkItem>

        {isAccountUpgradable && (
          <HighlightedItem
            icon="arrow_up"
            onClick={() => {
              tippyInstance.close();
              emitter.emit('clickedUpgradeYourPlan', eventPosition);
            }}
          >
            {t('AccountMenu.upgradeYourPlan')}
          </HighlightedItem>
        )}

        <Divider />

        {isEngageButtonActive && (
          <NavList.LinkItem icon="user_multiple" onClick={onOpenEngage}>
            {t('AccountMenu.engage')}
          </NavList.LinkItem>
        )}

        <NavList.LinkItem
          icon="off"
          onClick={() => {
            tippyInstance.close();
            emitter.emit('clickedLogout', eventPosition);
          }}
          href={buildHrefFromRoute(routes.AUTH.LOGOUT.path)}
        >
          {t('AccountMenu.logout')}
        </NavList.LinkItem>
      </NavList>
    </StyledDropdown>
  );
};
